import { useAppNavigate } from "hooks/useAppNavigate";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import LoadingButton from "components/LoadingButton";
import ErrorMessage from "components/ErrorMessage";
import PageTitle from "components/PageTitle";
import PageMeta from "components/PageMeta";
import CheckboxField from "components/CheckboxField";
import LegalLink from "components/LegalLink";
import { updateUserProfile, isServiceUnavailableError } from "services/auth.service";
import { getUserData, setUserData, getFlowState, clearFlowState } from "../../storage";
import { useLoginPage } from "hooks/useLoginPage";
import TextInput from "components/TextInput";
import { UserData } from "types";
import { isValidPassword } from "utils/validators";
import { postAuthTokenToNative } from "utils/nativeBridge";
import { useText } from "context/TextProvider";
import { trackEvent } from "services/analytics.service";
import { AnalyticsEvent, AnalyticsFlow, AnalyticsStatus } from "services/analyticsEvents";
import { useScreenTimeTracking } from "hooks/useScreenTimeTracking";

const ProfileUpdate = (): JSX.Element => {
  const text = useText();
  const state = useLocation().state as { email?: string } | null;
  const appNavigate = useAppNavigate();
  const { configId, loginPageConfig } = useLoginPage();
  useScreenTimeTracking(AnalyticsEvent.TIME_ON_PROFILE_UPDATE_SCREEN);
  const email = state?.email || getFlowState(configId).email || "";
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [sendEmails, setSendEmails] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName) {
      setError(text.profileUpdate.firstNameRequired);
      return;
    }
    if (!lastName) {
      setError(text.profileUpdate.lastNameRequired);
      return;
    }
    if (!password) {
      setError(text.profileUpdate.newPasswordRequired);
      return;
    }
    if (!isValidPassword(password)) {
      setError(text.profileUpdate.passwordRequirements);
      return;
    }
    setError("");
    setLoading(true);
    try {
      const userData = getUserData();
      if (!userData) return;
      const userId = userData.user.id;
      const updatedUser = await updateUserProfile({
        email,
        firstName,
        lastName,
        password,
        userId,
        url: loginPageConfig?.urls?.update_profile,
        accessToken: userData.token,
        approveTermsOfUse: agreeTerms ? "ON" : "OFF",
        approveMarketing: sendEmails ? "ON" : "OFF",
      });
      setUserData({
        ...userData,
        user: updatedUser.user,
      });
      const data = getUserData();
      if (!data) return;
      trackEvent(AnalyticsEvent.PROFILE_UPDATE_SUCCEEDED, { flow: AnalyticsFlow.STANDARD, status: AnalyticsStatus.SUCCEEDED });
      postAuthTokenToNative({
        token: data.token,
        refreshToken: data.refreshToken,
        tokenExpirationInstant: data.tokenExpirationInstant,
        refreshUrl: loginPageConfig?.urls?.refresh_url,
      });
      clearFlowState(configId);
      appNavigate(`/`);
    } catch (err) {
      setError(isServiceUnavailableError(err) ? text.common.serviceUnavailable : text.profileUpdate.updateFailed);
      trackEvent(AnalyticsEvent.PROFILE_UPDATE_FAILED, { flow: AnalyticsFlow.STANDARD, status: AnalyticsStatus.FAILED });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageMeta name="Profile Update" />
      <PageTitle>{text.profileUpdate.title}</PageTitle>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <TextInput
        id="firstName"
        type="text"
        label={text.profileUpdate.firstNameLabel}
        value={firstName}
        placeholder={text.profileUpdate.firstNamePlaceholder}
        onChange={(e) => setFirstName(e.target.value)}
      />
      <TextInput
        id="lastName"
        type="text"
        label={text.profileUpdate.lastNameLabel}
        value={lastName}
        placeholder={text.profileUpdate.lastNamePlaceholder}
        onChange={(e) => setLastName(e.target.value)}
      />
      <TextInput
        id="newPassword"
        type="password"
        label={text.profileUpdate.newPasswordLabel}
        value={password}
        placeholder={text.profileUpdate.newPasswordPlaceholder}
        onChange={(e) => setPassword(e.target.value)}
      />
      <CheckboxField
        id="agreeTerms"
        checked={agreeTerms}
        onChange={(e) => setAgreeTerms(e.target.checked)}
        label={
          <>
            {text.profileUpdate.agreeTermsLabel}{" "}
            <LegalLink href={loginPageConfig?.login_page_style?.termsAndConditionsUrl}>
              {text.profileUpdate.termsAndConditionsLinkText}
            </LegalLink>
          </>
        }
      />
      <CheckboxField
        id="sendEmails"
        checked={sendEmails}
        onChange={(e) => setSendEmails(e.target.checked)}
        label={text.profileUpdate.sendEmailsLabel}
      />
      <LoadingButton
        loading={loading}
        onClick={handleSubmit}
        sx={{
          width: "100%",
          height: "3.5rem",
        }}
        disabled={loading || !agreeTerms}
      >
        {text.profileUpdate.updateButton}
      </LoadingButton>
    </>
  );
};

export default ProfileUpdate;
